import { type FC } from "react";

import { Label } from "@fronterahealth/frontera-ui-components";

import { FAILURE_TAGS } from "@components/EvalReview/constants";

interface FailureTagPickerProps {
  selected: string[];
  disabled: boolean;
  onToggle: (tag: string) => void;
}

/**
 * Structured reasons for a low score.
 *
 * Worth more per keystroke than the notes field — these are what the
 * results view filters by, whereas prose gets read once.
 */
export const FailureTagPicker: FC<FailureTagPickerProps> = ({ selected, disabled, onToggle }) => (
  <div>
    <Label size="small" colorType="primary" className="uppercase tracking-wide">
      What went wrong
    </Label>
    <div className="mt-1.5 flex flex-wrap gap-2">
      {FAILURE_TAGS.map((tag) => {
        const isOn = selected.includes(tag.value);
        return (
          <button
            key={tag.value}
            type="button"
            disabled={disabled}
            aria-pressed={isOn}
            // Blur on pointer click so the panel's key bindings resume —
            // see ScoreGrid, which has the full note.
            onClick={(event) => {
              if (event.detail > 0) event.currentTarget.blur();
              onToggle(tag.value);
            }}
            className={`inline-flex items-center gap-x-1.5 rounded-md border px-2.5 py-1 text-xs transition-colors ${
              isOn
                ? "border-rio-600 bg-rio-600 text-white"
                : "border-border-medium bg-bg-surface text-text-secondary hover:border-rio-600"
            } ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
          >
            <span className={`font-mono ${isOn ? "text-white/70" : "text-text-tertiary"}`}>{tag.key}</span>
            {tag.label}
          </button>
        );
      })}
    </div>
  </div>
);
